<script setup lang="ts">
import { useForm } from 'vee-validate';
import * as yup from 'yup';
// Stores
const { currentSite } = storeToRefs(adminStore());

// Composables
const { t } = useI18n();
const { updateSite } = useSiteApi();
const { daysListAsOptions } = useConstants();
const { searchAnAdress } = useGouvernementClient();
const { handleSubmit, meta, resetForm, values, setFieldValue } = useForm({
  validationSchema: yup.object({
    name: yup.string().required(t('global.field_required')),
    production_start_weekday: yup.string().required(t('global.field_required')),
    address: yup.string().required(t('global.field_required')),
    lat: yup.number().required(t('global.field_required')),
    long: yup.number().required(t('global.field_required')),
  }),
});

// Data
const search = ref('');
const loading = ref(false);

// Methods
const submit = handleSubmit((values) => {
  loading.value = true;
  if (currentSite.value?.id) {
    updateSite(currentSite.value?.id, values);
  }
  loading.value = false;
});

const { data: address, refresh } = useAsyncData(
  () => {
    return searchAnAdress(search.value);
  },
  {
    immediate: false,
  },
);

// Computed
const convertAddressToItems = computed(() => {
  return (
    address.value?.features.map((feature) => {
      return {
        id: feature.properties.label,
        label: feature.properties.label,
      };
    }) ?? []
  );
});
const getLatAndLong = computed(() => {
  const addressFeature = address.value?.features.find((feature) => feature.properties.label === values.address);
  return {
    lat: addressFeature?.geometry.coordinates[1] ?? null,
    long: addressFeature?.geometry.coordinates[0] ?? null,
  };
});

// Lifecycle
onBeforeMount(() => {
  resetForm({
    values: {
      name: currentSite.value?.name,
      production_start_weekday: currentSite.value?.production_start_weekday,
      address: currentSite.value?.address,
      lat: currentSite.value?.lat,
      long: currentSite.value?.long,
    },
  });

  watchEffect(() => {
    if (search.value?.length > 5) {
      refresh();
    }
  });
  watch(
    () => values.address,
    () => {
      if (values.address) {
        setFieldValue('lat', getLatAndLong.value.lat);
        setFieldValue('long', getLatAndLong.value.long);
      }
    },
  );
});
</script>
<template>
  <ui-card :title="$t('global.informations')">
    <div class="grid grid-cols-2 gap-x-4">
      <ui-form-input-text name="name" class="col-span-1" :label="$t('global.name')" required :placeholder="$t('global.name')" />
      <nrjx-input-select
        name="production_start_weekday"
        class="col-span-1"
        :label="$t('global.production_start_week_day')"
        :placeholder="$t('global.production_start_week_day')"
        item-key="key"
        item-label="label"
        fit-to-select
        :items="daysListAsOptions"
      />

      <nrjx-input-combobox
        v-model:search="search"
        class="col-span-2"
        name="address"
        :label="$t('global.address')"
        :items="convertAddressToItems"
        item-key="id"
        item-label="label"
        fit-to-select
      />

      <ui-form-input-text
        name="lat"
        class="col-span-1"
        :label="$t('admin.latitude')"
        required
        disabled
        hide-details
        :placeholder="$t('admin.latitude')"
      />
      <ui-form-input-text
        name="long"
        class="col-span-1"
        hide-details
        :label="$t('admin.longitude')"
        required
        disabled
        :placeholder="$t('admin.longitude')"
      />
    </div>

    <template #footer>
      <ui-button :loading="loading" :color="meta.dirty ? 'primary' : 'disabled'" @click="submit()">{{ $t('global.save') }}</ui-button>
    </template>
  </ui-card>
</template>
